// What is worked on inside a Block, switched by hand (the Block's `works`):
// which Case or Action is being worked on now and how long each one took.
import type {
  BlockWork,
  LinkTarget,
  TimeBlock,
  TimeBlockLink,
} from "@trameria/core/timeblocker-shared";

export const sameTarget = (
  w: Pick<BlockWork, "caseId" | "stepId">,
  t: LinkTarget,
) => w.caseId === t.caseId && (w.stepId ?? null) === (t.stepId ?? null);

/** The works still open on a Block that runs (none when it is over). */
export function openWorks(block: TimeBlock, nowMs: number): BlockWork[] {
  if (!block.startedAt || block.endedAt) return [];
  if (Date.parse(block.endAt) <= nowMs) return [];
  return block.works.filter((w) => w.endedAt === null);
}

/** The Cases and Actions being worked on now (several may be), as the Block links them. */
export function workingLinks(block: TimeBlock, nowMs: number): TimeBlockLink[] {
  const open = openWorks(block, nowMs);
  return block.links.filter((l) => open.some((w) => sameTarget(w, l)));
}

/** How long a Case or Action was worked on inside the Block (an open one counts up to now, within the Block's time). */
export function workedMs(
  block: TimeBlock,
  target: LinkTarget,
  nowMs: number,
): number {
  const limit = Math.min(nowMs, Date.parse(block.endedAt ?? block.endAt));
  let sum = 0;
  for (const w of block.works) {
    if (!sameTarget(w, target)) continue;
    const end = Math.min(
      w.endedAt === null ? limit : Date.parse(w.endedAt),
      limit,
    );
    sum += Math.max(0, end - Date.parse(w.startedAt));
  }
  return sum;
}

/** The works after starting `target` at `at` (as they were when it is being worked on). */
export function startedWorks(
  works: BlockWork[],
  target: LinkTarget,
  at: string,
): BlockWork[] {
  if (works.some((w) => w.endedAt === null && sameTarget(w, target)))
    return works;
  return [
    ...works,
    {
      caseId: target.caseId,
      stepId: target.stepId ?? null,
      startedAt: at,
      endedAt: null,
    },
  ];
}

/** The works after stopping the open one on `target` at `at`. */
export const stoppedWorks = (
  works: BlockWork[],
  target: LinkTarget,
  at: string,
): BlockWork[] =>
  works.map((w) =>
    w.endedAt === null && sameTarget(w, target) ? { ...w, endedAt: at } : w,
  );
